import { useCallback, useRef, useState } from 'react';

// Minimal streaming helper for POST SSE-like endpoints.
export function useSSEApply() {
  const [events, setEvents] = useState([]);
  const [running, setRunning] = useState(false);
  const abortRef = useRef(null);

  const run = useCallback(async ({ url, token, body }) => {
    setEvents([]);
    setRunning(true);

    // Validate token before making request
    if (!token) {
      setRunning(false);
      throw new Error('Authentication required. Please log in again.');
    }

    const controller = new AbortController();
    abortRef.current = controller;

    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (fetchError) {
      setRunning(false);
      // Handle network errors, CORS issues, or aborted requests
      if (fetchError.name === 'AbortError') {
        throw new Error('Request cancelled');
      }
      throw new Error(`Network error: ${fetchError.message || 'Failed to connect to server'}`);
    }

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      setRunning(false);
      // Parse error detail if JSON
      let errorMsg = text || `Request failed (${res.status})`;
      try {
        const errJson = JSON.parse(text);
        if (errJson.detail) errorMsg = errJson.detail;
      } catch {
        // keep original text
      }
      throw new Error(errorMsg);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE frames separated by double newlines
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        frames.forEach((frame) => {
          const line = frame
            .split('\n')
            .map((l) => l.trim())
            .find((l) => l.startsWith('data:'));
          if (!line) return;
          const jsonStr = line.replace(/^data:\s*/, '');
          try {
            const evt = JSON.parse(jsonStr);
            setEvents((prev) => [...prev, evt]);
          } catch {
            // ignore malformed JSON
          }
        });
      }
    } catch (streamError) {
      setRunning(false);
      throw new Error(`Stream error: ${streamError.message || 'Connection lost'}`);
    }

    setRunning(false);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort?.();
    setRunning(false);
  }, []);

  return { events, running, run, cancel };
}
